import { WarningFilled } from '@ant-design/icons';
import { Button, message, Modal, Tooltip } from 'antd';
import EntityChangeRequests from 'components/EntityChangeRequests';
import EntityLogoEditor from 'components/EntityLogoEditor';
import FormSelect from 'components/Form/FormSelect';
import HmsEntityFormController from 'components/Form/HmsEntityFormController';
import HmsEntityFormGridBasic from 'components/Form/HmsEntityFormGridBasic';
import HmsField from 'components/Form/HmsField';
import HmsTabbedTables from 'components/HmsTabbedTables';
import { ListContext } from 'components/ListsProvider';
import PlayerJerseys from 'components/Player/PlayerJerseys';
import PlayerPositions from 'components/Player/PlayerPositions';
import PlayerTeams from 'components/Player/PlayerTeams';
import PlayerLevelTag from 'components/PlayerLevelTag';
import useEntities from 'hooks/useEntities';
import useLists from 'hooks/useLists';
import useLoadEntity from 'hooks/useLoadEntity';
import useOrganization from 'hooks/useOrganization';
import { useContext, useMemo, useRef } from 'react';
import useLoginStore from 'store/loginStore';
import useRequiredActionsStore from 'store/requiredActionsStore';
import { patchEntity } from 'utils/apiFunctions';
import { getDefaultCardWidth } from 'utils/uiPreferences';

const PlayerProfile = () => {
  const currentUser = useLoginStore(s => s.user);
  const updateIsLoggedIn = useLoginStore(s => s.updateIsLoggedIn);
  const currentRole = useLoginStore(s => s.getCurrentRole)();
  const entities = useEntities();
  const entity = entities.Player;
  const reloadRequiredActions = useRequiredActionsStore(s => s.reload);
  const { organization } = useOrganization();
  const approvalRequired = !!organization?.playerApprovalRequired;   // HMS-76

  const playerId = currentUser.playerId;
  
  const { isLoading, isReloading, relationsLoading, isError, isNotFound, data, reload, relationsData, reloadRelations } = useLoadEntity({
    entity,
    overrideId: playerId,
    relations: [
      'Teams',
      'Jerseys>Team',
      'ListPositions',
    ].join(','),
  });

  const { orgPreferences } = useContext(ListContext);

  const ref = useRef();
  const tabbedTablesRef = useRef(null);
  const tabbedTablesParentRef = useRef(null);

  // Change requests
  const { relationsData: changeRequestsData, reloadRelations: reloadChangeRequests } = useLoadEntity({
    entity,
    overrideId: playerId,
    relations: [
      'ChangeRequests',
    ].join(',')
  });
  
  const unprocessedChangeRequestCount = useMemo(() => changeRequestsData?.ChangeRequests?.filter(r => r.status == 'NEW')?.length ?? 0, [changeRequestsData]);
  const showChangeRequestDot = unprocessedChangeRequestCount > 0;
  
  // Determine if player can edit based on status
  const isInvited = data?.status === 'INVITED';
  const isPendingApproval = approvalRequired && data?.status === 'PREAPPROVED';
  const isLocked = !isInvited;
  const canCreateChangeRequest = isLocked;
  
  const { ListPlayerLevel } = useLists();
  const ListPlayerLevelWithIcons = ListPlayerLevel.map((l) => ({
    value: l[entities.ListPlayerLevel.primaryKey],
    label: <span className="flex gap-2 items-center"><PlayerLevelTag listPlayerLevel={l} /> {l.name}</span>
  }));
  
  const changeRequestFields = [
    { value: "listPlayerLevelId", label: 'Player level', type: 'select', options: ListPlayerLevelWithIcons },
    { value: 'firstName', },
    { value: "lastName", },
    { value: "birthday", },
    { value: "gender", },
    { value: "stick", },
    { value: "status", },
    { value: "height", },
    { value: "weight", },
    { value: "phone", },
    { value: "email", },
    { value: "countryBirth", },
    { value: "cityBirth", },
    { value: "country", },
    { value: "city", },
    { value: "publicProfileUrl", },
    { value: "AddTeam", label: 'Add Team' },
    { value: "RemoveTeam", label: 'Remove Team' },
    { value: "Position", label: 'Position' },
    { value: "Jersey", label: 'Jersey' },
    { value: "Other", label: 'Other / Comment' },
  ].map(f => ({
    ...f,
    label: f.label ?? entity.fields[f.value].displayName,
    type: f.type ?? 'text',
  }));

  // Handle profile activation
  const handleActivateProfile = (isDirty) => {
    const hasUnsavedChanges = isDirty;
    
    Modal.confirm({
      title: 'Confirm profile activation',
      content: (
        <div>
          <p>After activating the profile, you will only be able to change your profile using change requests.</p>
          {approvalRequired && (
            <p className="mt-2">
              Your profile will then be reviewed by the organization administrator. You can register for competitions and pay the fee right away, but you can be added to a game line up only after the approval.
            </p>
          )}
          {hasUnsavedChanges && (
            <p className="text-orange-600 font-medium mt-2">
              ⚠️ You have unsaved changes, please save them first.
            </p>
          )}
        </div>
      ),
      okText: hasUnsavedChanges ? 'Save Changes & Activate' : 'Activate',
      cancelText: 'Cancel',
      okButtonProps: { disabled: hasUnsavedChanges, size: 'small' },
      cancelButtonProps: { size: 'small' },
      onOk: async () => {
        try {
          
          // Update player status to ACTIVE (HMS-76: with approval required the profile goes to PREAPPROVED and waits for the admin)
          let response = await patchEntity(
            data?.organizationId, 
            entity, 
            playerId, 
            { status: approvalRequired ? 'PREAPPROVED' : 'ACTIVE' },
            currentRole
          );
          
          if (response.status === 200) {
            message.success(approvalRequired ? 'Profile activated successfully! It is now waiting for approval by the organization administrator.' : 'Profile activated successfully!');
            // Reload the data to reflect the new status
            reload();
            updateIsLoggedIn();
            reloadRequiredActions({ showNext: true });
          } else {
            message.error('Failed to activate profile. Please try again.');
          }
        } catch (error) {
          console.error('Error activating profile:', error);
          message.error('Failed to activate profile. Please try again.');
        }
      },
    });
  };

  // Handle post-save success message
  const handleAfterSave = () => {
    if (isInvited) {
      message.success('Profile updated successfully!');
    }
  };

  // Show loading if still loading player data
  if (!playerId) {
    return <div>Loading your profile...</div>;
  }

  // Show error if player not found
  if (isNotFound) {
    return <div>Player profile not found.</div>;
  }


  return (
    <>
      <HmsEntityFormController
        {... {
          ref, entity, id: playerId, isLoading: isLoading , isReloading, isError,
          isNotFound, data, reload, canCreateChangeRequest, isLocked,
          changeRequestFields, reloadChangeRequests, 
          afterSave: handleAfterSave,
          canSave: data?.status !== 'ACTIVE' && !isPendingApproval
        }}
        header={({ isDirty, lastValidationErrors, data: formData }) => {
          if (isLoading || relationsLoading) {
            return;
          }

          const missingItems = [];
          
        //   if (isDirty) {
        //     missingItems.push('Save your changes.');
        //   } else {
        //     if (lastValidationErrors.length > 0) {
        //       missingItems.push('Fix validation errors');
        //     }
        //   }
          
          // if (orgPreferences.requiredFields.includes('jerseyNumber') && (relationsData?.Jerseys?.length ?? 0) == 0) {
          //   missingItems.push(
          //     <span>
          //       Jersey is requested and missing.
          //     </span>
          //   );
          // }
          // if (orgPreferences.requiredFields.includes('listPositionId') && (relationsData?.ListPositions?.length ?? 0) == 0) {
          //   missingItems.push(
          //     <span>
          //       Position is requested and missing.
          //     </span>
          //   );
          // }

          if (orgPreferences.requiredFields?.includes("teamId") && !relationsData?.Teams?.length) {
            missingItems.push(
              <span>
                Team is requested and missing. 
              </span>
            );
          }

          // adamq: should we allow save and change to active if the following is missing?

          return (
            <>
              {data?.status == 'INVITED' &&
                <div className={`bg-[#e5f5ff] text-[#2280ff] border-[#cee7ff] rounded-md mb-3 p-3 ${getDefaultCardWidth(true)} border-[1px] flex items-center`}>
                  <span>
                    Please review your profile, complete any missing fields and click the 'Activate' button when you are ready.
                  </span>
                  <Button 
                    type="primary" 
                    onClick={() => handleActivateProfile(isDirty)}
                    className="ml-4"
                     size="small"
                  >
                    Activate
                  </Button>
                </div>
              }

              {isPendingApproval &&
                <div className={`bg-[#ddeeff] text-[#2280ff] border-[#cee7ff] rounded-md mb-3 p-3 ${getDefaultCardWidth(true)} border-[1px] flex items-center`}>
                  <span>
                    Your profile has been activated and is waiting for approval by the organization administrator. You can already register for competitions and pay the fee. You can be added to a game line up once your profile is approved.
                  </span>
                </div>
              }

              {missingItems.length > 0 && (
                <div className="mt-4 mb-4 p-4 border border-orange-200 bg-orange-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <WarningFilled className="text-orange-600 text-lg" />
                    <h4 className="text-orange-800 font-medium m-0">Required Information Missing</h4>
                  </div>
                  <p className="text-orange-700 text-sm mb-3">Your team manager or admin must assign one. You can use change requests to request these changes.</p>
                  <ul className="mt-2 mb-0 ml-2 flex flex-col gap-2">
                    {missingItems.map((item, idx) => (
                      <li key={idx} className="text-orange-700 flex items-center gap-2">
                        <span className="text-orange-500">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          );
        }}

        form={(
          <>
            <div className="flex flex-col desktop:flex-row w-full">
              <div className="flex items-center justify-center ml-4 mr-6 mb-5 desktop:mb-0 ">
                <EntityLogoEditor entity={entities.Player} id={playerId} data={data} size={112} disabled={false} />
              </div>
              <div className="flex-auto">
                <HmsEntityFormGridBasic>
                  <Tooltip title={data?.status !== 'ACTIVE' && !isPendingApproval ? "This field can only be changed via a change request, after the profile has been activated" : ""}>
                    <div>
                      <HmsField name="firstName" fieldItemProps={{ disabled: true }} />
                    </div>
                  </Tooltip>
                  <Tooltip title={data?.status !== 'ACTIVE' && !isPendingApproval ? "This field can only be changed via a change request, after the profile has been activated" : ""}>
                    <div>
                      <HmsField name="lastName" fieldItemProps={{ disabled: true }} />
                    </div>
                  </Tooltip>
                  <HmsField name="birthday" fieldItemProps={{ disabled: isLocked }} required={orgPreferences.requiredFields?.includes("birthday")} />
                  <HmsField name="gender" fieldItemProps={{ disabled: isLocked }} required={orgPreferences.requiredFields?.includes("gender")} />
                  <HmsField name="stick" fieldItemProps={{ disabled: isLocked }} required={orgPreferences.requiredFields?.includes("stick")} />
                  <Tooltip title={data?.status !== 'ACTIVE' && !isPendingApproval && data?.listPlayerLevelId ? "This field can only be changed via a change request, after the profile has been activated" : ""}>
                    <div>
                      <FormSelect name="listPlayerLevelId" label="Player level" options={ListPlayerLevelWithIcons} fieldItemProps={{ disabled: !isInvited || !!data?.listPlayerLevelId }}
                        required={orgPreferences.requiredFields?.includes("listPlayerLevelId")} />
                    </div>
                  </Tooltip>
                  <HmsField name="status" fieldItemProps={{ disabled: true }} />
                </HmsEntityFormGridBasic>
              </div>
            </div>

            <h4>Personal</h4>
            <HmsEntityFormGridBasic>
              <HmsField name="height" addonAfter="cm" fieldItemProps={{ disabled: isLocked }} required={orgPreferences.requiredFields?.includes("height")} />
              <HmsField name="weight" addonAfter="kg" fieldItemProps={{ disabled: isLocked }} required={orgPreferences.requiredFields?.includes("weight")} />
              <HmsField name="phone" fieldItemProps={{ disabled: isLocked }} required={orgPreferences.requiredFields?.includes("phone")} />
              <Tooltip title={data?.status !== 'ACTIVE' && !isPendingApproval ? "This field can only be changed via a change request, after the profile has been activated" : ""}>
                <div>
                  <HmsField name="email" fieldItemProps={{ disabled: true }} required={orgPreferences.requiredFields?.includes("email")} />
                </div>
              </Tooltip>
            </HmsEntityFormGridBasic>

            <h4>Location</h4>
            <HmsEntityFormGridBasic>
              <HmsField name="countryBirth" fieldItemProps={{ disabled: isLocked }} required={orgPreferences.requiredFields?.includes("countryBirth")} />
              <HmsField name="cityBirth" fieldItemProps={{ disabled: isLocked }} required={orgPreferences.requiredFields?.includes("cityBirth")} />
              <HmsField name="country" fieldItemProps={{ disabled: isLocked }} required={orgPreferences.requiredFields?.includes("country")} />
              <HmsField name="city" fieldItemProps={{ disabled: isLocked }} required={orgPreferences.requiredFields?.includes("city")} />
            </HmsEntityFormGridBasic>


          </>
        )}
        footer={(
            <div className="mt-4">
              <div className="mt-4" ref={tabbedTablesParentRef}>
                <HmsTabbedTables
                  ref={tabbedTablesRef}
                  tables={[
                    {
                      key: 'Jerseys',
                      title: "Jerseys" + (relationsData?.Jerseys ? (' (' + relationsData?.Jerseys?.length + ')') : ''),
                      children: <PlayerJerseys data={relationsData?.Jerseys} sourceEntityId={data?.playerId} sourceEntityData={data} onChange={reloadRelations} teams={relationsData?.Teams} />,
                    },
                    {
                      key: 'Positions',
                      title: "Positions" + (relationsData?.ListPositions ? (' (' + relationsData?.ListPositions?.length + ')') : ''),
                      children: <PlayerPositions data={relationsData?.ListPositions} sourceEntityId={data?.playerId} sourceEntityData={data} onChange={reloadRelations} />,
                    },
                    {
                      key: 'Teams',
                      title: "Teams" + (relationsData?.Teams ? (' (' + relationsData?.Teams?.length + ')') : ''),
                      children: <PlayerTeams data={relationsData?.Teams} sourceEntityId={data?.playerId} sourceEntityData={data} onChange={reloadRelations} />,
                    },
                    {
                        key: 'ChangeRequests',
                        title: "Change Requests" + (changeRequestsData?.ChangeRequests ? (' (' + (unprocessedChangeRequestCount > 0 ? '' + unprocessedChangeRequestCount + '/' : '') + changeRequestsData?.ChangeRequests?.length + ')') : ''),
                        children: <EntityChangeRequests sourceEntityName={entity.name} sourceEntityId={data?.[entity.primaryKey]} data={changeRequestsData?.ChangeRequests} onChange={reloadChangeRequests} />,
                        showDot: showChangeRequestDot,
                      },
                  ].filter(t => t)}
                />
              </div>
            </div>
          )}
      />
    </>
  );
};

export default PlayerProfile; 